import type { MetricBasis, MetricWindow } from '@elutide/shared';
import {
  BASIS_DEFINITION,
  BASIS_LABEL,
  fullTimestamp,
  formatMoney,
  relativeAge,
  type AgeClass,
  type AgeState,
  ageStateFor,
} from '@/lib/format';
import { useNow } from '@/lib/hooks';

export interface BasisBadgeProps {
  basis: MetricBasis;
  window?: MetricWindow | null;
  partial?: boolean;
  /** Extra bases for a composite figure. Renders as "WOO PAID · fees EST". */
  also?: readonly { basis: MetricBasis; note?: string }[];
}

/**
 * Every number on screen carries one of these. The vocabulary is fixed; a screen may not
 * invent a label, and hovering states the definition in one sentence.
 */
export function BasisBadge({ basis, window: metricWindow, partial = false, also }: BasisBadgeProps) {
  const windowNote = metricWindow ? ` Window: ${metricWindow.label} (${metricWindow.tz}).` : '';
  const title = `${BASIS_DEFINITION[basis]}${windowNote}${
    partial ? ' Source coverage for this window is incomplete.' : ''
  }`;
  const composite = (also ?? [])
    .map((entry) => `${entry.note ? `${entry.note} ` : ''}${BASIS_LABEL[entry.basis]}`)
    .join(' · ');

  return (
    <span className={`o-basis${partial ? ' o-basis--partial' : ''}`} title={title}>
      {BASIS_LABEL[basis]}
      {composite ? ` · ${composite}` : ''}
      {partial ? ' · PARTIAL' : ''}
    </span>
  );
}

export interface AgeStampProps {
  asOf: string | null | undefined;
  klass: AgeClass;
  /** For the model class, the snapshot the model was built from. */
  builtFrom?: string | null;
  label?: string;
}

/**
 * Dot plus relative time. Fresh, stale and dead thresholds come from the data class, which
 * is set once per metric and never per screen. A dead card keeps its numbers at reduced
 * opacity; it never blanks and never renders 0 for unknown.
 */
export function AgeStamp({ asOf, klass, builtFrom, label }: AgeStampProps) {
  const now = useNow();
  const state = ageStateFor(asOf, klass, now);
  const text =
    klass === 'model' && builtFrom
      ? `built from ${relativeAge(builtFrom, now)}`
      : relativeAge(asOf, now);

  return (
    <span
      className={`o-age${state === 'fresh' || state === 'unknown' ? '' : ` o-age--${state}`}`}
      title={asOf ? fullTimestamp(asOf) : 'No timestamp reported.'}
    >
      <span className="o-age__dot" aria-hidden="true" />
      <span>
        {label ? `${label} ` : ''}
        {text}
        {state === 'dead' ? ' stale' : ''}
      </span>
    </span>
  );
}

export function ageStateLabel(state: AgeState): string {
  switch (state) {
    case 'fresh':
      return 'Data is current.';
    case 'stale':
      return 'Data is behind. It is still the last known value.';
    case 'dead':
      return 'Data is stale. Showing the last known value, not a zero.';
    default:
      return 'No data age reported.';
  }
}

export interface AgeTextProps {
  at: string | null | undefined;
}

/** Relative under 24 hours, then a short date. Full timestamp on hover. */
export function AgeText({ at }: AgeTextProps) {
  const now = useNow();
  return (
    <time
      dateTime={at ?? undefined}
      title={at ? fullTimestamp(at) : 'unknown'}
      className="o-mono"
    >
      {relativeAge(at, now)}
    </time>
  );
}

export interface MoneyTextProps {
  cents: number | null;
  showCents?: boolean;
  /** Colour crit only when a negative is bad. A refund in a ledger is not bad. */
  tone?: 'neutral' | 'ok' | 'warn' | 'crit';
  nullText?: string;
}

export function MoneyText({ cents, showCents, tone = 'neutral', nullText }: MoneyTextProps) {
  const text = formatMoney(cents, {
    ...(showCents === undefined ? {} : { showCents }),
    ...(nullText === undefined ? {} : { nullText }),
  });
  const style = tone === 'neutral' ? undefined : { color: `var(--${tone})` };
  return (
    <span className="o-mono o-num" style={style}>
      {text}
    </span>
  );
}
